import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { googleSheets } from '../config';

const Header = ({ onMenuClick, toggleSidebar, isDarkMode, setIsDarkMode, players }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isUsingGoogleSheets, setIsUsingGoogleSheets] = useState(googleSheets.useGoogleSheetsForOdds);
  const [sheetsStatus, setSheetsStatus] = useState('unknown'); // 'unknown', 'connected', 'error'

  // Get current page label
  const getCurrentPageLabel = () => {
    const currentLink = navLinks.find(link => link.path === currentPath);
    return currentLink ? currentLink.label :'Player Dashboard';
  };

  const navLinks = [
    { path: '/teamstats', label: 'Team Stats' },
    { path: '/defense-vs-position', label: 'Defense vs Position' },
    { path: '/hit-rates', label: 'Hit Rates' },
    { path: '/team-dashboard', label: 'Team Dashboard' },
    { path: '/venue-stats', label: 'Venue Stats' },
    { path: '/matchups', label: 'Matchups' },
    { path: '/box-score', label: 'Box Score' },
  ];

  // Function to navigate to a random player
  const navigateToRandomPlayer = () => {
    if (players && players.length > 0) {
      const randomIndex = Math.floor(Math.random() * players.length);
      const randomPlayer = players[randomIndex];
      if (randomPlayer && randomPlayer.player_name) {
        // Encode the player name for URL
        const encodedPlayerName = encodeURIComponent(randomPlayer.player_name);
        navigate(`/player/${encodedPlayerName}`);
      }
    }
  };

  // Check Google Sheets connection status on component mount
  useEffect(() => {
    if (isUsingGoogleSheets) {
      const checkConnection = async () => {
        try {
          const url = `https://docs.google.com/spreadsheets/d/${googleSheets.oddsSheetId}/export?format=csv&gid=${googleSheets.oddsSheetGid}`;
          const response = await fetch(url);
          
          if (response.ok) {
            console.log('Google Sheets connection successful');
            setSheetsStatus('connected');
          } else {
            console.error(`Google Sheets connection failed: ${response.status} ${response.statusText}`);
            setSheetsStatus('error');
          }
        } catch (error) {
          console.error('Error checking Google Sheets connection:', error);
          setSheetsStatus('error');
        }
      };
      
      checkConnection();
    } else {
      console.log('Using local CSV data source');
    }
  }, [isUsingGoogleSheets]);

  return (
    <header className="relative h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div className="h-full max-w-7xl mx-auto flex items-center justify-between px-4">
        {/* Menu Button */}
        <button
          onClick={onMenuClick}
          className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none md:hidden transition-transform duration-300 hover:scale-110 active:scale-95"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Title with improved responsiveness */}
        <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center md:relative md:left-0 md:transform-none">
          <button 
            onClick={navigateToRandomPlayer} 
            className={`flex items-center transition-transform duration-300 hover:scale-105 ${currentPath === '/' ? 'font-bold text-accent dark:text-accent-light' : 'text-gray-900 dark:text-white'}`}
          >
            <div className="flex flex-col items-center md:items-start">
              <span className="text-lg font-semibold whitespace-nowrap">NRL Prop Tool</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">by BetSniper</span>
            </div>
          </button>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-6">
          {navLinks.map(({ path, label }) => (
            <Link
              key={path}
              to={path}
              className={`text-sm transition-all duration-300 hover:scale-110 hover:font-medium relative ${
                currentPath === path 
                  ? 'text-accent dark:text-accent-light font-medium after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-full after:bg-accent dark:after:bg-accent-light' 
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-accent dark:after:bg-accent-light hover:after:w-full after:transition-all after:duration-300'
              }`}
            >
              {label}
            </Link>
          ))}
        </nav>

        {/* Dark Mode Toggle and Mobile Nav Toggle */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-200"
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>
          
          {/* Mobile Navigation Button */}
          <button
            onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
            className="md:hidden p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none"
          >
            <svg 
              className={`w-6 h-6 transition-transform duration-200 ${isMobileNavOpen ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div 
        className={`
          absolute left-0 right-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-lg md:hidden
          transition-all duration-200 ease-in-out z-50
          ${isMobileNavOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}
        `}
      >
        <nav className="max-w-7xl mx-auto py-2 px-4">
          <div className="grid grid-cols-2 gap-2">
            {navLinks.map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                onClick={() => setIsMobileNavOpen(false)}
                className={`px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                  currentPath === path
                    ? 'bg-accent/10 dark:bg-accent-light/10 text-accent dark:text-accent-light font-medium shadow-sm border border-accent/20 dark:border-accent-light/20'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {label}
              </Link>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header; 